import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOSchema from "@/components/base/SEOSchema";
import { reputationItems } from "@/mocks/reputation";

export default function AboutPage() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [statusMsg, setStatusMsg] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setStatusMsg("提交成功！我们会尽快与您联系。");
      setIsError(false);
      setFormData({ name: "", email: "", phone: "", message: "" });
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatusMsg("请填写所有必填字段");
      setIsError(true);
      return;
    }

    if (formData.message.length > 500) {
      setStatusMsg("留言内容不能超过500字");
      setIsError(true);
      return;
    }

    try {
      const formDataObj = new FormData(form);
      const res = await fetch("https://readdy.ai/api/form/d9phhj5i2vhsdh2d34h0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setStatusMsg("提交成功！我们会尽快与您联系。");
        setIsError(false);
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else if (serverMsg?.includes("spam") || serverMsg?.includes("form data is spam")) {
        setStatusMsg("提交成功！我们会尽快与您联系。");
        setIsError(false);
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        setStatusMsg(serverMsg || "提交失败，请稍后重试");
        setIsError(true);
      }
    } catch {
      setStatusMsg("网络错误，请稍后重试");
      setIsError(true);
    }
  };

  return (
    <>
      <SEOSchema
        type="WebPage"
        data={{
          name: "关于我们 - 17c com一起草在线",
          description: "了解17c com一起草在线的品牌故事、口碑评价与联系方式。免费在线视频播放平台，提供海量高清视频内容。",
          url: "https://17c.com/about",
        }}
      />
      <SEOSchema
        type="Organization"
        data={{
          name: "17c com一起草在线",
          url: "https://17c.com",
          description: "免费在线视频播放平台，提供海量高清视频内容",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            email: "contact@17c.com",
            availableLanguage: ["Chinese"],
          },
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t("breadcrumbHome"), href: "/" },
                { label: t("about") },
              ]}
            />

            <section className="pb-14">
              <div className="max-w-[900px] mx-auto">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-4">
                  {t("about")}
                </h1>
                <p className="text-center text-foreground-500 mb-12 max-w-[600px] mx-auto">
                  了解我们的品牌故事、服务理念与发展历程
                </p>
              </div>

              <section className="mb-14" aria-labelledby="brand-intro-heading">
                <h2 id="brand-intro-heading" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-2">
                  {t("brandIntro")}
                </h2>
                <p className="text-center text-foreground-500 text-sm mb-8">关于17c com一起草在线</p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-[1100px] mx-auto">
                  <div className="rounded-lg overflow-hidden bg-background-200 aspect-[4/3]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20workspace%20with%20large%20screens%20displaying%20video%20content%2C%20warm%20natural%20lighting%2C%20creative%20studio%20atmosphere%2C%20clean%20aesthetic%2C%20editorial%20photography%20with%20soft%20shadows%2C%20professional%20environment&width=800&height=600&seq=about-brand-01&orientation=landscape"
                      alt="17c一起草在线办公环境"
                      title="17c一起草在线 - 办公室"
                      className="w-full h-full object-cover"
                      width="800"
                      height="600"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-4">
                      我们的故事
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      17c com一起草在线成立于2020年，是一个致力于为全球中文用户提供优质在线视频内容的免费播放平台。我们的创始团队来自互联网和影视行业的资深从业者，怀着"让每个人都能轻松享受精彩视频"的初心，打造了这个内容丰富、体验流畅的视频平台。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      经过多年发展，平台已积累超过50,000部视频资源，涵盖电影、电视剧、综艺、动漫、纪录片、体育、音乐、游戏等八大分类，日均服务超过800万活跃用户。我们始终坚持免费开放的运营理念，用技术驱动内容分发，为用户创造最佳的在线观看体验。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      未来，17c com一起草在线将继续深耕内容生态，引入更多优质视频资源，优化播放技术，让每一位用户都能在这里找到属于自己的精彩世界。
                    </p>
                  </div>
                </div>
              </section>

              <section className="py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 mb-14" aria-labelledby="values-heading">
                <div className="max-w-[1100px] mx-auto">
                  <h2 id="values-heading" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-10">
                    我们的核心价值
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      {
                        icon: "ri-shield-check-line",
                        title: "安全可靠",
                        desc: "采用先进的网络安全技术，保障用户隐私和数据安全",
                      },
                      {
                        icon: "ri-flashlight-line",
                        title: "极速流畅",
                        desc: "全球CDN加速，智能码率调节，畅享零卡顿播放体验",
                      },
                      {
                        icon: "ri-stack-line",
                        title: "内容丰富",
                        desc: "海量正版视频资源，八大分类覆盖所有内容需求",
                      },
                      {
                        icon: "ri-user-heart-line",
                        title: "用户至上",
                        desc: "以用户体验为核心，持续优化产品功能与服务",
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="text-center p-6 rounded-lg bg-background-50">
                        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                          <i className={`${item.icon} text-2xl`}></i>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2">
                          {item.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-14" aria-labelledby="reputation-heading">
                <h2 id="reputation-heading" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-2">
                  {t("reputation")}
                </h2>
                <p className="text-center text-foreground-500 text-sm mb-8">来自用户的真实评价</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1100px] mx-auto">
                  {reputationItems.map((item) => (
                    <div
                      key={item.id}
                      className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: item.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-secondary-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        &ldquo;{item.content}&rdquo;
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground-900">
                          {item.userName}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {item.location} · {item.date}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6" aria-labelledby="contact-heading">
                <div className="max-w-[1100px] mx-auto">
                  <h2 id="contact-heading" className="text-xl md:text-2xl font-bold text-foreground-950 font-heading text-center mb-2">
                    {t("contactUs")}
                  </h2>
                  <p className="text-center text-foreground-500 text-sm mb-10">
                    有任何问题或建议？欢迎联系我们
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <div className="space-y-5">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                            <i className="ri-mail-line text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-foreground-900">电子邮箱</h4>
                            <p className="text-sm text-foreground-500">contact@17c.com</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                            <i className="ri-phone-line text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-foreground-900">客服热线</h4>
                            <p className="text-sm text-foreground-500">400-888-8888</p>
                            <p className="text-xs text-foreground-400 mt-0.5">工作日 9:00 - 18:00</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 shrink-0">
                            <i className="ri-map-pin-line text-lg"></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-foreground-900">公司地址</h4>
                            <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8">
                        <iframe
                          title="公司位置"
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3061.831599489854!2d116.4669541!3d39.908722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f052f10796250d%3A0xc09e58a4114fe7ff!2z5YyX5Lqs5biC5pyd6Ziz5Yy65bu66Zu86LevODjlj7c!5e0!3m2!1szh-CN!2sus!4v1"
                          width="100%"
                          height="200"
                          className="border-0 rounded-lg"
                          allowFullScreen={false}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          aria-label="公司位置地图"
                        ></iframe>
                      </div>
                    </div>

                    <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                      <h3 className="text-base font-semibold text-foreground-900 font-heading mb-5">
                        发送消息
                      </h3>
                      <form
                        onSubmit={handleSubmit}
                        data-readdy-form
                        className="space-y-4"
                      >
                        <input
                          type="text"
                          name="company_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden={true}
                          readOnly
                          className="honeypot-field"
                        />
                        <div>
                          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            {t("name")} <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-name"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                            placeholder="请输入您的姓名"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            {t("email")} <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                            placeholder="请输入您的邮箱"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            {t("phone")}
                          </label>
                          <input
                            id="contact-phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300"
                            placeholder="请输入您的电话（选填）"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                            {t("message")} <span className="text-primary-500">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            rows={4}
                            maxLength={500}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            className="w-full px-3 py-2 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none"
                            placeholder="请输入您的留言（最多500字）"
                            required
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1 text-right">
                            {formData.message.length}/500
                          </p>
                        </div>
                        <button
                          type="submit"
                          className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {t("submit")}
                        </button>
                        {statusMsg && (
                          <p
                            className={`text-sm text-center ${isError ? "text-red-500" : "text-accent-600"}`}
                          >
                            {statusMsg}
                          </p>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </section>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}